/* eslint-disable react/jsx-props-no-spreading */
import React, { useEffect } from 'react';
import {
  Box,
  Typography,
  Stack,
} from '@mui/material';
import {
  getFieldProps,
  InputPhone,
  getFormProps,
  Validate,
  useForm,
  getSubmitButtonProps,
  TimezoneSelector,
} from '@marageti/z4-lib';
import { Controller } from 'react-hook-form';
import { LoadingButton } from '@mui/lab';
import * as z from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { ReactComponent as ZicassoSVG } from '../../assets/svg/zicasso.svg';
import { ConfirmPhoneViewProps } from './types';
import './activate.scss';

const schema = z.object({
  phone: z.object({
    prefix: z.string(),
    number: z.string(),
  }).refine((value) => {
    if (!value.number) return true;
    return Validate.phone(`${value.prefix}${value.number}`);
  }, { message: 'Please enter a valid number.' }),
  timeZone: z.object({
    name: z.string(),
    rawOffsetInMinutes: z.any(),
  })
    .refine((data) => data.name && data.rawOffsetInMinutes !== undefined, {
      message: 'Required',
      path: [],
    }),
});

const ConfirmPhoneView = ({
  defaultValues,
  onSubmit,
}: ConfirmPhoneViewProps): React.ReactElement => {
  const {
    handleSubmit,
    formState: {
      errors, isSubmitting, isValid,
    },
    control,
    reset,
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues,
    mode: 'onChange',
  });

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Stack
        spacing={3}
        sx={{
          width: { mobile: '342px', desktop: '333px' },
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Stack flexDirection="column" spacing={4} alignItems="center">
          <Stack spacing={2} alignItems="center">
            <ZicassoSVG aria-label="Zicasso" />
            <Typography variant="h2">
              Confirm your Phone Number
            </Typography>
            <Typography color="text.secondary" style={{ textAlign: 'center' }}>
              Our top travel specialists are in high demand and prioritize travelers who provide a contact number. We respect and protect your privacy.
            </Typography>
          </Stack>
          <Stack component="form" {...getFormProps(handleSubmit(onSubmit))} sx={{ width: '100%' }}>
            <Controller
              control={control}
              name="phone"
              render={({ field }) => (
                <InputPhone
                  id="phone"
                  label="Phone Number"
                  {...field}
                  {...getFieldProps('phone', errors)}
                />
              )}
            />

            <Controller
              name="timeZone"
              control={control}
              render={({ field }) => (
                <TimezoneSelector
                  required
                  id="timeZone-selector"
                  label="Timezone"
                  {...getFieldProps('timeZone', errors)}
                  {...field}
                />
              )}
            />

            <LoadingButton
              variant="contained"
              type="submit"
              sx={{ mt: 3 }}
              {...getSubmitButtonProps(isSubmitting, isValid)}
            >
              Confirm
            </LoadingButton>
          </Stack>
        </Stack>
      </Stack>
    </Box>
  );
};

export default ConfirmPhoneView;
